// The Properties panel's building blocks (issue 1575, in issue 1541's look): a section as a bordered card with a gold
// ▾/▸ chevron and a one-line summary that a closed header still shows; the identity header's round, gold-ringed
// portrait (the token's own picture, or its initial); the Vitals' HP bar, stat tiles and condition chips. A closed
// section's controls stay mounted but `hidden` — absent from the page and from a screen reader — so what was typed in
// one survives closing it, and opening it is one press.
import { useEffect, useId, useState, type ReactNode } from 'react';
import type { PanelKind } from '@dragons-table/vtt-shared';
import { pictureUrl } from '../../assets';
import { initialsOf } from '../../board/tokenStatus';
import { useSectionOpen } from '../panelPrefs';

/**
 * One section of the panel: its header a button that opens and closes it, remembered for this person and this kind of
 * thing; the summary beside it whether open or closed. `testid` names the card (`section-<key>`).
 */
export function Section({ kind, id, title, summary, open: fallback, children, danger }: {
  kind: PanelKind; id: string; title: string; summary?: string; open: boolean; children: ReactNode; danger?: boolean;
}) {
  const [open, setOpen] = useSectionOpen(kind, id, fallback);
  const body = useId();
  return (
    <section className={`vtt-card ${danger ? 'border-red-900' : ''}`} data-testid={`section-${id}`} data-open={open ? 'true' : 'false'}>
      <div className={`flex flex-nowrap items-center gap-2 px-2.5 py-1.5 ${open ? 'border-b border-vtt-line-soft' : ''}`}>
        <button type="button" className="flex shrink-0 items-center gap-2 text-left vtt-section-head" aria-expanded={open} aria-controls={body}
          data-testid={`section-head-${id}`} onClick={() => setOpen(!open)}>
          <span aria-hidden="true" className="w-2.5 shrink-0 text-[0.625rem] text-vtt-gold">{open ? '▾' : '▸'}</span>
          {title}
        </button>
        {summary && <span className="ml-auto min-w-0 truncate text-[0.71875rem] text-vtt-muted" data-testid={`summary-${id}`} title={summary}>{summary}</span>}
      </div>
      <div id={body} hidden={!open} className="space-y-2 px-2.5 py-2">{children}</div>
    </section>
  );
}

/** The identity header's portrait: 52 px, round, a gold ring; the token's own picture, fetched as every picture is, or its initial. */
export function Portrait({ name, asset }: { name: string | undefined; asset: string | null | undefined }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setUrl(null);
    if (asset) void pictureUrl(asset).then((u) => { if (live) setUrl(u); }).catch(() => undefined);
    return () => { live = false; };
  }, [asset]);
  const initial = initialsOf(name ?? '').slice(0, 1) || '?';
  return (
    <span className="grid h-[3.25rem] w-[3.25rem] shrink-0 place-items-center overflow-hidden rounded-full border-2 border-vtt-gold bg-vtt-raised font-display text-lg font-bold text-vtt-fg" data-testid="portrait" aria-hidden="true">
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : initial}
    </span>
  );
}

/** The Vitals' HP bar: the site's HP red on its dark track, and "5 / 7" beside it. */
export function HpBar({ current, max, label }: { current: number; max: number; label: string }) {
  const k = max > 0 ? Math.max(0, Math.min(1, current / max)) : 0;
  return (
    <div className="flex items-center gap-2" data-testid="hp-bar" role="img" aria-label={`${label} ${current} of ${max}`}>
      <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-vtt-hp-track">
        <span className="block h-full rounded-full bg-vtt-hp" style={{ width: `${Math.round(k * 100)}%` }} />
      </span>
      <span className="min-w-[3rem] text-right text-sm font-bold text-vtt-fg" aria-hidden="true">{current} / {max}</span>
    </div>
  );
}

/** The Vitals' tiles: a number large over its name small (AC, Touch, Flat-footed, Init); a number not known is a dash. */
export function StatTiles({ tiles }: { tiles: Array<{ label: string; value: string | null }> }) {
  if (!tiles.length) return null;
  return (
    <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${tiles.length}, minmax(0, 1fr))` }} data-testid="stat-tiles">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-[3px] border border-vtt-line bg-vtt-raised px-1 pb-1 pt-1.5 text-center">
          <b className="block text-base font-bold leading-tight text-vtt-fg">{t.value ?? '—'}</b>
          <span className="block truncate text-[0.59375rem] uppercase tracking-[0.08em] text-vtt-muted">{t.label}</span>
        </div>
      ))}
    </div>
  );
}

/** The Vitals' chips: each condition, with its duration where the turn order gives one; and the dashed "+ condition". */
export function ConditionChips({ chips, onAdd }: { chips: string[]; onAdd?: () => void }) {
  if (!chips.length && !onAdd) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="condition-chips">
      {chips.map((c) => <span key={c} className="rounded-full border border-amber-800 bg-vtt-gold-fill/15 px-2 py-px text-[0.6875rem] text-amber-200">{c}</span>)}
      {onAdd && <button type="button" className="rounded-full border border-dashed border-vtt-line px-2 py-px text-[0.6875rem] text-vtt-muted hover:text-vtt-fg" onClick={onAdd}>+ condition</button>}
    </div>
  );
}
